import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Header from "@/components/layout/Header"
import Footer from "@/components/layout/Footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/contexts/AuthContext"
import { getMyOrders } from "@/api/orderApi"
import { getAddresses, addAddress, setDefaultAddress, deleteAddress } from "@/api/addressApi"
import { getWishlist } from "@/api/wishlistApi"
import { getReviewables, getMyReviews, addReview } from "@/api/reviewApi"
import { updateMemberInfo } from "@/api/authApi"

// 💡 수정됨: WAIT(입금대기) 추가
const statusMap: Record<string, string> = { CREATED: "주문접수", WAIT: "입금대기", PAID: "결제완료", PREPARING: "배송준비중", SHIPPING: "배송중", DONE: "배송완료", CANCELED: "주문취소" }

export default function MyPage() {
    const navigate = useNavigate()
    const { user, isLoggedIn, isLoading, refreshAuth } = useAuth()

    const [orders, setOrders] = useState<any[]>([])
    const [addresses, setAddresses] = useState<any[]>([])
    const [wishlist, setWishlist] = useState<any[]>([])

    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
    const [profileForm, setProfileForm] = useState({ memberName: "", phone: "", password: "", passwordConfirm: "" })

    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
    const [addressModalView, setAddressModalView] = useState<"LIST" | "ADD">("LIST")
    const [newAddress, setNewAddress] = useState({ receiverName: "", receiverPhone: "", zipcode: "", address1: "", address2: "", defaultYn: "N" })

    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
    const [reviewTab, setReviewTab] = useState<"AVAILABLE" | "WRITTEN">("AVAILABLE")
    const [reviewables, setReviewables] = useState<any[]>([])
    const [myReviews, setMyReviews] = useState<any[]>([])

    const [writeTarget, setWriteTarget] = useState<any>(null)
    const [rating, setRating] = useState(5)
    const [content, setContent] = useState("")

    const loadAddresses = async () => { try { const res = await getAddresses(); setAddresses(res.data || []) } catch (e) { } }
    const loadWishlist = async () => { try { const res = await getWishlist(); setWishlist(res.data || []) } catch (e) { } }
    const loadReviewData = async () => {
        try {
            const availRes = await getReviewables()
            const writtenRes = await getMyReviews()
            setReviewables(availRes.data || [])
            setMyReviews(writtenRes.data || [])
        } catch (error) { console.error("리뷰 데이터 로드 실패:", error) }
    }

    useEffect(() => {
        if (isLoading) return
        if (!isLoggedIn) { alert("로그인이 필요합니다."); navigate("/login"); return; }

        const fetchOrders = async () => { try { const res = await getMyOrders(); setOrders(res.data || []) } catch (e) {} }
        fetchOrders(); loadAddresses(); loadWishlist(); loadReviewData();
    }, [isLoggedIn, isLoading, navigate])

    const handleOpenProfileModal = () => {
        setProfileForm({ memberName: user?.memberName || "", phone: user?.phone || "", password: "", passwordConfirm: "" })
        setIsProfileModalOpen(true)
    }

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!profileForm.memberName) return alert("이름을 입력해주세요.")
        if (profileForm.password && profileForm.password !== profileForm.passwordConfirm) {
            return alert("변경할 비밀번호가 서로 일치하지 않습니다.")
        }
        try {
            await updateMemberInfo({
                memberName: profileForm.memberName,
                phone: profileForm.phone,
                password: profileForm.password
            })
            alert("회원정보가 성공적으로 수정되었습니다.")
            await refreshAuth()
            setIsProfileModalOpen(false)
        } catch (error: any) {
            alert(error.response?.data?.message || "정보 수정에 실패했습니다.")
        }
    }

    const handleSetDefault = async (addressId: number) => {
        if (window.confirm("이 배송지를 기본 배송지로 설정하시겠습니까?")) { await setDefaultAddress(addressId); loadAddresses(); }
    }
    const handleDelete = async (addressId: number) => {
        if (window.confirm("정말 이 배송지를 삭제하시겠습니까?")) { await deleteAddress(addressId); loadAddresses(); }
    }
    const handleAddSubmit = async () => {
        if (!newAddress.receiverName || !newAddress.receiverPhone || !newAddress.address1) return alert("필수 정보를 모두 입력해주세요.")
        try {
            await addAddress(newAddress); alert("배송지가 추가되었습니다.");
            setNewAddress({ receiverName: "", receiverPhone: "", zipcode: "", address1: "", address2: "", defaultYn: "N" })
            setAddressModalView("LIST"); loadAddresses();
        } catch (error) { alert("배송지 추가 중 오류가 발생했습니다.") }
    }

    const handleReviewSubmit = async () => {
        if (!content.trim()) return alert("리뷰 내용을 입력해주세요.")
        try {
            await addReview({ productId: writeTarget.productId, orderId: writeTarget.orderId, rating, content })
            alert("리뷰가 등록되었습니다! 포인트가 지급됩니다.")
            setWriteTarget(null); setContent(""); setRating(5);
            setReviewTab("WRITTEN"); loadReviewData();
        } catch (error) { alert("리뷰 등록에 실패했습니다.") }
    }

    if (isLoading) return <div className="p-20 text-center text-slate-500">정보를 불러오는 중입니다...</div>
    if (!user) return null

    // 💡 수정됨: PAID(결제완료) 뿐만 아니라 WAIT(입금대기) 건수도 포함되도록 변경
    const paidCount = orders.filter(o => o.orderStatus === "PAID" || o.orderStatus === "WAIT").length
    const preparingCount = orders.filter(o => o.orderStatus === "PREPARING").length
    const shippingCount = orders.filter(o => o.orderStatus === "SHIPPING").length
    const doneCount = orders.filter(o => o.orderStatus === "DONE").length
    const defaultAddress = addresses.find(addr => addr.defaultYn?.toUpperCase() === 'Y')

    const renderStars = (currentRating: number, interactive = false) => {
        return (
          <div className="flex gap-1 text-2xl">
              {[1, 2, 3, 4, 5].map(star => (
                <span key={star} onClick={() => interactive && setRating(star)} className={`cursor-pointer transition-colors ${star <= currentRating ? "text-yellow-400" : "text-slate-200"}`}>★</span>
              ))}
          </div>
        )
    }

    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
          <Header />
          <main className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
              <div className="mb-8"><p className="text-sm text-slate-500">홈 &gt; 마이페이지</p><h1 className="mt-2 text-3xl font-bold">마이페이지</h1></div>

              <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
                  <aside className="space-y-4">
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                          <CardContent className="p-6">
                              <div className="flex items-center gap-4">
                                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-lg font-bold text-white">{user.memberName.charAt(0)}</div>
                                  <div className="min-w-0 flex-1">
                                      <p className="text-lg font-bold truncate">{user.memberName}님</p>
                                      <p className="text-sm text-slate-500 truncate">{user.loginId}</p>
                                  </div>
                              </div>
                              <Button onClick={handleOpenProfileModal} className="mt-5 w-full rounded-2xl bg-slate-900 text-white hover:bg-slate-800">
                                  회원정보 수정
                              </Button>
                          </CardContent>
                      </Card>
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                          <CardContent className="p-4">
                              <nav className="space-y-1">
                                  <button className="w-full rounded-xl bg-slate-900 px-4 py-3 text-left text-sm font-medium text-white">주문 내역</button>
                                  <button className="w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-100" onClick={() => setIsAddressModalOpen(true)}>배송지 관리</button>
                                  <button className="w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-100 flex justify-between items-center" onClick={() => setIsReviewModalOpen(true)}>
                                      <span>리뷰 관리</span>
                                      {reviewables.length > 0 && <span className="bg-rose-500 text-white text-xs px-2 py-1 rounded-full">{reviewables.length}</span>}
                                  </button>
                                  <button
                                    className="w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
                                    onClick={() => navigate("/cs/inquiries")}
                                  >
                                      문의 내역
                                  </button>
                              </nav>
                          </CardContent>
                      </Card>
                  </aside>

                  <section className="space-y-6">
                      {/* 1. 주문 현황 */}
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                          <CardHeader><CardTitle className="text-xl">주문 현황</CardTitle></CardHeader>
                          <CardContent>
                              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                  <div className="rounded-2xl bg-slate-50 p-5 text-center">
                                      {/* 💡 텍스트 수정: 결제/입금완료 (후결제 건수도 포함되었기 때문) */}
                                      <p className="text-sm text-slate-500">결제/입금완료</p>
                                      <p className="mt-2 text-2xl font-bold">{paidCount}</p>
                                  </div>
                                  <div className="rounded-2xl bg-slate-50 p-5 text-center"><p className="text-sm text-slate-500">배송준비중</p><p className="mt-2 text-2xl font-bold">{preparingCount}</p></div>
                                  <div className="rounded-2xl bg-slate-50 p-5 text-center"><p className="text-sm text-slate-500">배송중</p><p className="mt-2 text-2xl font-bold">{shippingCount}</p></div>
                                  <div className="rounded-2xl bg-slate-50 p-5 text-center"><p className="text-sm text-slate-500">배송완료</p><p className="mt-2 text-2xl font-bold">{doneCount}</p></div>
                              </div>
                          </CardContent>
                      </Card>

                      {/* 3. 최근 주문 내역 */}
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                          <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-xl">최근 주문 내역</CardTitle><Button variant="outline" className="rounded-xl">전체 보기</Button></CardHeader>
                          <CardContent className="space-y-4">
                              {orders.length === 0 ? (<div className="rounded-2xl bg-slate-50 p-10 text-center text-slate-500">주문 내역이 없습니다.</div>) : (
                                orders.slice(0, 5).map((order, idx) => (
                                  <div key={order.orderNo}>
                                      <div className="flex flex-col gap-3 rounded-2xl bg-slate-50 p-5 lg:flex-row lg:items-center lg:justify-between">
                                          <div><p className="text-sm text-slate-500">주문번호 {order.orderNo}</p><p className="mt-1 text-sm text-slate-500">{order.createdAt.substring(0, 10)}</p><h3 className="mt-2 text-lg font-semibold">{order.displayProductName}</h3></div>
                                          <div className="flex flex-col items-start gap-2 lg:items-end">
                                              <p className="text-lg font-bold">{order.totalAmount.toLocaleString("ko-KR")}원</p>

                                              {/* 💡 수정됨: WAIT(입금대기) 상태일 경우 붉은색 배경의 라벨로 시인성 확보 */}
                                              <span className={`rounded-full px-3 py-1 text-sm font-medium shadow-sm border ${
                                                order.orderStatus === 'WAIT'
                                                  ? 'bg-rose-50 text-rose-600 border-rose-100'
                                                  : 'bg-white text-slate-700 border-slate-200'
                                              }`}>
                                                  {statusMap[order.orderStatus] || order.orderStatus}
                                              </span>

                                              <div className="flex gap-2">
                                                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => navigate(`/order/${order.orderNo}`)}>상세보기</Button>
                                                  {order.orderStatus === "DONE" ? (
                                                    <Button size="sm" className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setIsReviewModalOpen(true)}>리뷰 쓰기</Button>
                                                  ) : (
                                                    <Button size="sm" className="rounded-xl bg-slate-900 text-white hover:bg-slate-800">재구매</Button>
                                                  )}
                                              </div>
                                          </div>
                                      </div>
                                      {idx !== Math.min(orders.length, 5) - 1 && <Separator className="my-2 opacity-0" />}
                                  </div>
                                ))
                              )}
                          </CardContent>
                      </Card>

                      {/* 4. 기본 배송지 */}
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                          <CardHeader className="flex flex-row items-center justify-between">
                              <CardTitle className="text-xl">기본 배송지</CardTitle>
                              <Button variant="outline" className="rounded-xl" onClick={() => { setIsAddressModalOpen(true); setAddressModalView("LIST"); }}>배송지 관리</Button>
                          </CardHeader>
                          <CardContent>
                              {defaultAddress ? (
                                <div className="rounded-2xl bg-slate-50 p-5">
                                    <p className="text-base font-bold text-slate-900">{defaultAddress.receiverName}</p>
                                    <p className="mt-2 text-sm text-slate-600 font-medium">{defaultAddress.receiverPhone}</p>
                                    <p className="mt-2 text-sm leading-6 text-slate-600">
                                        {defaultAddress.zipcode && `[${defaultAddress.zipcode}] `}
                                        {defaultAddress.address1} {defaultAddress.address2}
                                    </p>
                                    <span className="mt-3 inline-flex rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 border border-indigo-100">기본 배송지</span>
                                </div>
                              ) : (
                                <div className="rounded-2xl bg-slate-50 p-10 text-center text-slate-500">등록된 기본 배송지가 없습니다.</div>
                              )}
                          </CardContent>
                      </Card>

                      {/* 5. 찜한 상품 목록 */}
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                          <CardHeader className="flex flex-row items-center justify-between">
                              <CardTitle className="text-xl">찜한 상품</CardTitle>
                              <Button variant="outline" className="rounded-xl">전체 보기</Button>
                          </CardHeader>
                          <CardContent>
                              {wishlist.length === 0 ? (
                                <div className="rounded-2xl bg-slate-50 p-10 text-center text-slate-500">찜한 상품이 없습니다.</div>
                              ) : (
                                <div className="grid gap-4 sm:grid-cols-2">
                                    {wishlist.slice(0, 4).map((product) => (
                                      <div key={product.wishlistId} className="flex gap-4 rounded-2xl border border-slate-200 p-4 bg-white">
                                          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-slate-100 cursor-pointer" onClick={() => navigate(`/product/${product.productId}`)}>
                                              {product.thumbnailUrl ? <img src={product.thumbnailUrl} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-xs text-slate-400">No Img</div>}
                                          </div>
                                          <div className="flex min-w-0 flex-1 flex-col justify-between">
                                              <div>
                                                  <p className="text-xs text-slate-500">MG퀵플라워</p>
                                                  <h3 className="mt-1 text-base font-semibold truncate hover:underline cursor-pointer" onClick={() => navigate(`/product/${product.productId}`)}>{product.productName}</h3>
                                              </div>
                                              <div className="mt-3 flex items-center justify-between">
                                                  <p className="text-lg font-bold">{product.price.toLocaleString("ko-KR")}원</p>
                                                  <Button size="sm" className="rounded-xl bg-slate-900 text-white hover:bg-slate-800">담기</Button>
                                              </div>
                                          </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                          </CardContent>
                      </Card>
                  </section>
              </div>
          </main>

          {/* 회원정보 수정 모달 */}
          {isProfileModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                <Card className="w-full max-w-md rounded-[28px] shadow-xl">
                    <div className="flex items-center justify-between p-6 border-b border-slate-100">
                        <h2 className="text-xl font-bold">회원정보 수정</h2>
                        <button className="text-slate-400 hover:text-slate-700 text-2xl font-light" onClick={() => setIsProfileModalOpen(false)}>&times;</button>
                    </div>
                    <CardContent className="p-6">
                        <form onSubmit={handleProfileSubmit} className="space-y-4">
                            <div className="space-y-2"><Label>이름</Label><Input value={profileForm.memberName} onChange={e => setProfileForm({...profileForm, memberName: e.target.value})} className="h-11 rounded-xl" required /></div>
                            <div className="space-y-2"><Label>휴대폰 번호</Label><Input value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} placeholder="010-0000-0000" className="h-11 rounded-xl" /></div>
                            <Separator className="my-4" /><p className="text-xs text-slate-500 font-medium mb-2">* 비밀번호를 변경하지 않으려면 아래 칸을 비워두세요.</p>
                            <div className="space-y-2"><Label>새 비밀번호</Label><Input type="password" value={profileForm.password} onChange={e => setProfileForm({...profileForm, password: e.target.value})} placeholder="변경할 비밀번호 입력" className="h-11 rounded-xl" /></div>
                            <div className="space-y-2"><Label>새 비밀번호 확인</Label><Input type="password" value={profileForm.passwordConfirm} onChange={e => setProfileForm({...profileForm, passwordConfirm: e.target.value})} placeholder="변경할 비밀번호 다시 입력" className="h-11 rounded-xl" /></div>
                            <div className="flex gap-2 pt-4">
                                <Button type="button" variant="outline" className="flex-1 h-12 rounded-2xl" onClick={() => setIsProfileModalOpen(false)}>취소</Button>
                                <Button type="submit" className="flex-1 h-12 rounded-2xl bg-slate-900 text-white">저장하기</Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
          )}

          {/* 배송지 관리 모달 */}
          {isAddressModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-md rounded-[28px] bg-white shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
                    <div className="flex items-center justify-between p-6 border-b border-slate-100">
                        <h2 className="text-xl font-bold">{addressModalView === "LIST" ? "배송지 관리" : "새 배송지 추가"}</h2>
                        <button className="text-slate-400 hover:text-slate-700 text-2xl font-light" onClick={() => setIsAddressModalOpen(false)}>&times;</button>
                    </div>
                    <div className="p-6 overflow-y-auto">
                        {addressModalView === "LIST" ? (
                          <div className="space-y-4">
                              {addresses.length === 0 ? <p className="py-10 text-center text-slate-500 bg-slate-50 rounded-2xl">등록된 배송지가 없습니다.</p> : addresses.map(addr => (
                                <div key={addr.addressId} className="rounded-2xl border border-slate-200 p-4 relative">
                                    {addr.defaultYn === 'Y' && (<span className="absolute top-4 right-4 text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">기본</span>)}
                                    <p className="font-bold text-slate-900">{addr.receiverName}</p>
                                    <p className="text-sm text-slate-600 mt-1">{addr.receiverPhone}</p>
                                    <p className="text-sm text-slate-600 mt-1">[{addr.zipcode}] {addr.address1} {addr.address2}</p>
                                    <div className="mt-4 flex gap-2">
                                        {addr.defaultYn !== 'Y' && (<Button variant="outline" size="sm" className="flex-1 rounded-xl" onClick={() => handleSetDefault(addr.addressId)}>기본으로 설정</Button>)}
                                        <Button variant="ghost" size="sm" className="rounded-xl text-rose-500 hover:text-rose-700 hover:bg-rose-50" onClick={() => handleDelete(addr.addressId)}>삭제</Button>
                                    </div>
                                </div>
                              ))}
                              <Button className="w-full mt-4 h-12 rounded-2xl bg-slate-900 text-white" onClick={() => setAddressModalView("ADD")}>+ 새 배송지 추가</Button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                              <div className="space-y-2"><Label>받는 분</Label><Input value={newAddress.receiverName} onChange={e => setNewAddress({...newAddress, receiverName: e.target.value})} placeholder="이름 입력" /></div>
                              <div className="space-y-2"><Label>연락처</Label><Input value={newAddress.receiverPhone} onChange={e => setNewAddress({...newAddress, receiverPhone: e.target.value})} placeholder="010-0000-0000" /></div>
                              <div className="space-y-2"><Label>우편번호</Label><Input value={newAddress.zipcode} onChange={e => setNewAddress({...newAddress, zipcode: e.target.value})} placeholder="12345" /></div>
                              <div className="space-y-2"><Label>기본 주소</Label><Input value={newAddress.address1} onChange={e => setNewAddress({...newAddress, address1: e.target.value})} placeholder="기본 주소 입력" /></div>
                              <div className="space-y-2"><Label>상세 주소</Label><Input value={newAddress.address2} onChange={e => setNewAddress({...newAddress, address2: e.target.value})} placeholder="상세 주소 입력" /></div>
                              <label className="flex items-center gap-2 mt-4 cursor-pointer"><input type="checkbox" checked={newAddress.defaultYn === "Y"} onChange={e => setNewAddress({...newAddress, defaultYn: e.target.checked ? "Y" : "N"})} className="w-4 h-4" /><span className="text-sm font-medium">기본 배송지로 설정</span></label>
                              <div className="flex gap-2 mt-6">
                                  <Button variant="outline" className="flex-1 h-12 rounded-2xl" onClick={() => setAddressModalView("LIST")}>취소</Button>
                                  <Button className="flex-1 h-12 rounded-2xl bg-slate-900 text-white" onClick={handleAddSubmit}>저장하기</Button>
                              </div>
                          </div>
                        )}
                    </div>
                </div>
            </div>
          )}

          {/* 리뷰 관리 모달 */}
          {isReviewModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-lg rounded-[28px] bg-white shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
                    <div className="flex items-center justify-between p-6 border-b border-slate-100">
                        <h2 className="text-xl font-bold">{writeTarget ? "리뷰 작성" : "리뷰 관리"}</h2>
                        <button className="text-slate-400 hover:text-slate-700 text-2xl font-light" onClick={() => {setIsReviewModalOpen(false); setWriteTarget(null);}}>&times;</button>
                    </div>

                    <div className="p-6 overflow-y-auto">
                        {writeTarget ? (
                          <div className="space-y-6">
                              <div className="flex gap-4 items-center bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                  <div className="h-16 w-16 bg-white rounded-xl overflow-hidden shadow-sm">
                                      {writeTarget.thumbnailUrl ? <img src={writeTarget.thumbnailUrl} className="w-full h-full object-cover"/> : <div className="w-full h-full bg-slate-200"></div>}
                                  </div>
                                  <div>
                                      <p className="text-sm text-slate-500">주문일자: {writeTarget.orderDate.substring(0,10)}</p>
                                      <p className="font-bold">{writeTarget.productName}</p>
                                  </div>
                              </div>
                              <div className="text-center space-y-2"><p className="font-medium">상품은 만족하셨나요?</p><div className="flex justify-center">{renderStars(rating, true)}</div></div>
                              <textarea className="w-full h-32 p-4 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none" placeholder="상품에 대한 솔직한 평가를 남겨주세요." value={content} onChange={(e) => setContent(e.target.value)}></textarea>
                              <div className="flex gap-2">
                                  <Button variant="outline" className="flex-1 h-12 rounded-2xl" onClick={() => setWriteTarget(null)}>취소</Button>
                                  <Button className="flex-1 h-12 rounded-2xl bg-slate-900 text-white" onClick={handleReviewSubmit}>리뷰 등록</Button>
                              </div>
                          </div>
                        ) : (
                          <>
                              <div className="flex gap-2 mb-6 bg-slate-100 p-1 rounded-2xl">
                                  <button className={`flex-1 py-2 rounded-xl text-sm font-bold ${reviewTab === 'AVAILABLE' ? 'bg-white shadow-sm' : 'text-slate-500'}`} onClick={() => setReviewTab('AVAILABLE')}>
                                      작성 가능한 리뷰 <span className="text-rose-500">{reviewables.length}</span>
                                  </button>
                                  <button className={`flex-1 py-2 rounded-xl text-sm font-bold ${reviewTab === 'WRITTEN' ? 'bg-white shadow-sm' : 'text-slate-500'}`} onClick={() => setReviewTab('WRITTEN')}>내가 작성한 리뷰 {myReviews.length}</button>
                              </div>
                              <div className="space-y-4">
                                  {reviewTab === 'AVAILABLE' && (
                                    reviewables.length === 0 ? <p className="py-10 text-center text-slate-500">작성 가능한 리뷰가 없습니다.</p> :
                                      reviewables.map(item => (
                                        <div key={item.productId} className="flex gap-4 items-center p-4 rounded-2xl border border-slate-200">
                                            <div className="h-16 w-16 bg-slate-100 rounded-xl overflow-hidden">{item.thumbnailUrl && <img src={item.thumbnailUrl} className="w-full h-full object-cover"/>}</div>
                                            <div className="flex-1"><p className="font-bold text-sm truncate">{item.productName}</p><p className="text-xs text-slate-500 mt-1">결제일: {item.orderDate.substring(0,10)}</p></div>
                                            <Button size="sm" className="rounded-xl bg-slate-900 text-white" onClick={() => setWriteTarget(item)}>리뷰 쓰기</Button>
                                        </div>
                                      ))
                                  )}
                                  {reviewTab === 'WRITTEN' && (
                                    myReviews.length === 0 ? <p className="py-10 text-center text-slate-500">작성한 리뷰가 없습니다.</p> :
                                      myReviews.map(item => (
                                        <div key={item.reviewId} className="p-4 rounded-2xl border border-slate-200 space-y-3">
                                            <div className="flex gap-3 items-center border-b border-slate-100 pb-3"><div className="h-10 w-10 bg-slate-100 rounded-lg overflow-hidden">{item.thumbnailUrl && <img src={item.thumbnailUrl} className="w-full h-full object-cover"/>}</div><p className="font-bold text-sm truncate">{item.productName}</p></div>
                                            <div>{renderStars(item.rating)}<p className="text-sm mt-2 text-slate-700 whitespace-pre-wrap">{item.content}</p><p className="text-xs text-slate-400 mt-2">{item.createdAt.substring(0,10)} 작성</p></div>
                                        </div>
                                      ))
                                  )}
                              </div>
                          </>
                        )}
                    </div>
                </div>
            </div>
          )}
          <Footer />
      </div>
    )
}